import React, { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = [["not_delivered", "I paid but the deal/property was not delivered"], ["payment_not_received", "I did the work / handed over but was not paid"], ["fake_listing", "Fake or misrepresented listing"], ["overcharged", "Overcharged / hidden fees"], ["misbehaviour", "Misbehaviour or threats"], ["other", "Something else"]];

// Both sides of a deal can report cheating → stored in `disputes`, emailed to official@locanest.in, visible in Admin → Disputes.
export default function ReportProblemDialog({ booking, otherPartyName, onReported }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "", description: "", other_party_name: otherPartyName || "", amount: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async () => {
    if (!f.category) return toast.error("Choose what went wrong");
    if (f.description.trim().length < 20) return toast.error("Please describe the issue in at least 20 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/disputes", { booking_id: booking.id, category: f.category, description: f.description, other_party_name: f.other_party_name || null, amount_involved_inr: f.amount ? Number(f.amount) : null });
      setDone(data.short_id);
      toast.success(`Report #${data.short_id} sent to LocaNest — we've emailed you a confirmation`);
      onReported?.(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="rounded-md border-red-300 text-red-700 hover:bg-red-50" data-testid="report-problem-btn">
        <Flag size={14} className="mr-1.5" /> Report a problem / cheating
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-problem-dialog">
          <DialogHeader>
            <DialogTitle>Report a problem on deal {booking.agreement_number}</DialogTitle>
            <DialogDescription>Goes straight to the LocaNest team (official@locanest.in) and appears in the admin dashboard. {booking.amount_paise > 0 ? "Any payment held by LocaNest on this deal will be reviewed before release." : ""}</DialogDescription>
          </DialogHeader>
          {done ? (
            <div className="rounded-md bg-[#D4DEC6]/50 border border-[#2B3A67]/20 p-4 text-sm" data-testid="report-problem-success">
              Report <b>#{done}</b> received. We reply within 24 hours by email.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>What went wrong?</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger data-testid="report-category"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{CATS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`report-category-${k}`}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Other party&rsquo;s name</Label><Input value={f.other_party_name} onChange={(e) => setF({ ...f, other_party_name: e.target.value })} data-testid="report-other-party" /></div>
              <div><Label>Amount involved (₹, optional)</Label><Input inputMode="numeric" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value.replace(/\D/g, "").slice(0, 8) })} placeholder="e.g. 5000" data-testid="report-amount" /></div>
              <div><Label>What happened?</Label><Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value.slice(0, 3000) })} placeholder="Dates, what was promised, what was paid (and how), what actually happened…" data-testid="report-description" /></div>
            </div>
          )}
          {!done && <DialogFooter><Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="report-submit">{busy ? "Sending…" : "Send report to LocaNest"}</Button></DialogFooter>}
        </DialogContent>
      </Dialog>
    </>
  );
}
